import {
    customerRelationshipOptions,
    getCustomerRelationShip,
    processIdentifiers,
} from "../../features/search/Filters/utility";
import {
    fixSearchStringForPostgres,
    getStoredItem,
    lowerCaseAllExceptFirst,
} from "../../shared/Utils";
import { mapLocalJurisdictionsToCertificateJurisdictionCodes } from "../../features/search/Filters/mapLocalJurisdictionsToCertificateJurisdictionCodes";
import { CertificateStatuses, completeRequestStatuses } from "../../shared/constants";

// Helper functions
const getSearchValue = formState => {
    const { nameOrCode, nameOrCodeWhileLoading } = formState;
    return nameOrCodeWhileLoading || nameOrCode;
};

const getCertificateStatuses = certificateStatuses => {
    const stored = getStoredItem("certificateStatuses");
    return stored || certificateStatuses;
};

const getExemptionStatuses = (certificateFiltersState, statuses) => {
    const exemptionStatuses = [];

    const hasValidStatus = completeRequestStatuses.some(status =>
        certificateFiltersState?.certificateStatus?.includes(status)
    );

    if (hasValidStatus) {
        const completeStatus = statuses.find(
            status => status.name === CertificateStatuses.Complete
        )?.id;
        const pendingFutureStatus = statuses.find(
            status => status.name === CertificateStatuses.PENDING_FUTURE
        )?.id;

        if (completeStatus) exemptionStatuses.push(completeStatus);
        if (pendingFutureStatus) exemptionStatuses.push(pendingFutureStatus);
    } else if (
        certificateFiltersState?.certificateStatus?.includes(CertificateStatuses.PendingFuture)
    ) {
        const pendingFutureStatus = statuses.find(
            status => status.name === CertificateStatuses.PENDING_FUTURE
        )?.id;
        if (pendingFutureStatus) exemptionStatuses.push(pendingFutureStatus);
    } else if (
        certificateFiltersState?.certificateStatus?.includes(
            lowerCaseAllExceptFirst(CertificateStatuses.Pending)
        )
    ) {
        const pendingStatus = statuses.find(
            status => status.name === CertificateStatuses.Pending
        )?.id;
        if (pendingStatus) exemptionStatuses.push(pendingStatus);
    }

    return exemptionStatuses;
};

const getDateRangeValue = (dateAfter, dateBefore) => ({
    from: dateAfter || null,
    to: dateBefore || null,
});

const buildDateRanges = certificateFiltersState => {
    return {
        customerCreated: getDateRangeValue(
            certificateFiltersState.customerCreatedDateAfter,
            certificateFiltersState.customerCreatedDateBefore
        ),
        customerModified: getDateRangeValue(
            certificateFiltersState.customerModifiedDateAfter,
            certificateFiltersState.customerModifiedDateBefore
        ),
        certificateCreated: getDateRangeValue(
            certificateFiltersState.certificateCreatedDateAfter,
            certificateFiltersState.certificateCreatedDateBefore
        ),
        certificateModified: getDateRangeValue(
            certificateFiltersState.certificateModifiedDateAfter,
            certificateFiltersState.certificateModifiedDateBefore
        ),
        certificateEffective: getDateRangeValue(
            certificateFiltersState.certificateEffectiveDateAfter,
            certificateFiltersState.certificateEffectiveDateBefore
        ),
        certificateExpiration: getDateRangeValue(
            certificateFiltersState.certificateExpirationDateAfter,
            certificateFiltersState.certificateExpirationDateBefore
        ),
        certificateValidated: getDateRangeValue(
            certificateFiltersState.certificateValidatedDateAfter,
            certificateFiltersState.certificateValidatedDateBefore
        ),
    };
};

function certificateFiltersData(
    selectedColumns,
    getState,
    pageSize = 20,
    pageNumber = 0,
    isVendor = false
) {
    const { userId, accountId, role, activeCompany } = getState().session;
    const { formState, certificateFiltersState, sortColumn, certificateStatuses } =
        getState().certificate;

    const searchVal = getSearchValue(formState);
    const statuses = getCertificateStatuses(certificateStatuses);
    const exemptionStatuses = getExemptionStatuses(certificateFiltersState, statuses);
    const dateRanges = buildDateRanges(certificateFiltersState);

    return {
        // Pagination and sorting
        pageSize,
        pageNumber,
        selectedColumns,
        orderBy: sortColumn[0],
        orderByDirection: sortColumn[1] ? "ASC" : "DESC",

        // Search and filters
        nameOrCode: fixSearchStringForPostgres(searchVal),
        purchaseOrderNumber: certificateFiltersState.invoiceNumber,
        companyDivisionId: certificateFiltersState.companyGroup.companyDivisionId,
        clientIds: certificateFiltersState.companyGroup.companyIds,
        companyDivisionName: certificateFiltersState.companyGroup.companyDivisionName,

        // Boolean flags
        isPrimary: certificateFiltersState.isCertificateActive,
        isBillTo: getCustomerRelationShip(
            certificateFiltersState.customerRelationShip,
            customerRelationshipOptions.value.isBillTo
        ),
        isShipTo: getCustomerRelationShip(
            certificateFiltersState.customerRelationShip,
            customerRelationshipOptions.value.isShipTo
        ),
        isDuplicate: certificateFiltersState.isDuplicateRelation,
        isSingle: certificateFiltersState.isPurchaseOrder,
        isVendor: isVendor || certificateFiltersState.isVendorFilter,
        certificateImageExists: certificateFiltersState.isCertificateImage,

        // Date ranges
        customerCreatedFrom: dateRanges.customerCreated.from,
        customerCreatedTo: dateRanges.customerCreated.to,
        customerModifiedFrom: dateRanges.customerModified.from,
        customerModifiedTo: dateRanges.customerModified.to,
        certificateCreatedFrom: dateRanges.certificateCreated.from,
        certificateCreatedTo: dateRanges.certificateCreated.to,
        certificateModifiedFrom: dateRanges.certificateModified.from,
        certificateModifiedTo: dateRanges.certificateModified.to,
        certificateEffectiveFrom: dateRanges.certificateEffective.from,
        certificateEffectiveTo: dateRanges.certificateEffective.to,
        certificateExpirationFrom: dateRanges.certificateExpiration.from,
        certificateExpirationTo: dateRanges.certificateExpiration.to,
        certificateValidatedFrom: dateRanges.certificateValidated.from,
        certificateValidatedTo: dateRanges.certificateValidated.to,

        // Status and categories
        status: certificateFiltersState.certificateStatus,
        exemptionStatus: exemptionStatuses,
        exposureZones: certificateFiltersState.certificateRegions,
        exemptionReasons: certificateFiltersState.exemptReasons,
        invalidReasons: certificateFiltersState.invalidReasons,
        customerRegions: certificateFiltersState.customerRegions,
        productExemptionCategories: certificateFiltersState.productExemptionCategories,

        // Additional properties
        certificateHasCustomers: certificateFiltersState.certificateHasCustomers,
        activeCertificate: certificateFiltersState.isCertActive,
        certificateIds: processIdentifiers(certificateFiltersState.certificateIds, Number).filter(
            Number.isFinite
        ), // remove invalid numbers
        customerCodes: processIdentifiers(
            certificateFiltersState.customerCodes,
            fixSearchStringForPostgres
        ),

        // User info
        avalaraUserId: userId,
        avalaraUserRole: role,
        avaTaxAccountId: accountId,
        avaTaxCompanyId: activeCompany.id,

        // Custom fields and labels
        customerCertificateCustomFields: certificateFiltersState.customerCertificateCustomFields,
        customerCustomFields: certificateFiltersState.customerCustomFields,
        certificatesLabels: certificateFiltersState.certificatesLabels,
        excludeCertificatesLabels: certificateFiltersState.excludeCertificateLabels,
        customersLabels: certificateFiltersState.customersLabels,
        excludeCustomersLabels: certificateFiltersState.excludeCustomerLabels,
        reviewedBy: certificateFiltersState.reviewedBy,
        certificateCustomFields: certificateFiltersState.certificateCustomFields,
        certificateJurisdictionCodes: mapLocalJurisdictionsToCertificateJurisdictionCodes(
            certificateFiltersState.localJurisdictions
        ),
    };
}

export default certificateFiltersData;
